import React, { useMemo, useState } from 'react';
import { KeyRound, Save, User } from 'lucide-react';
import Alert from '../components/Alert';
import { authUserStorage, userApi } from '../services/api';

const getApiErrorMessage = (err: any, fallback: string) => {
    const translatedMessage = err?.response?.data?.translatedMessage;
    if (typeof translatedMessage === 'string' && translatedMessage.trim()) {
        return translatedMessage;
    }

    const apiMessage = err?.response?.data?.message;
    if (typeof apiMessage === 'string' && apiMessage.trim()) {
        return apiMessage;
    }

    if (Array.isArray(apiMessage)) {
        const joined = apiMessage
            .map((item: { message?: string }) => item?.message)
            .filter((message: string | undefined): message is string => Boolean(message))
            .join(' ');

        if (joined) {
            return joined;
        }
    }

    return fallback;
};

const Profile: React.FC = () => {
    const storedUser = authUserStorage.get();
    const originalName = storedUser?.name || '';

    const [name, setName] = useState(originalName);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [savingProfile, setSavingProfile] = useState(false);
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    const hasPasswordChange = Boolean(currentPassword || newPassword);
    const hasNameChange = useMemo(() => name.trim() !== originalName, [name, originalName]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFeedback(null);

        const trimmedName = name.trim();

        if (!trimmedName) {
            setFeedback({
                type: 'error',
                message: 'O nome não pode ficar vazio.',
            });
            return;
        }

        if (hasPasswordChange && !currentPassword) {
            setFeedback({
                type: 'error',
                message: 'Informe a senha atual para alterar a senha.',
            });
            return;
        }

        if (currentPassword && !newPassword) {
            setFeedback({
                type: 'error',
                message: 'Informe a nova senha.',
            });
            return;
        }

        if (newPassword && newPassword.length < 8) {
            setFeedback({
                type: 'error',
                message: 'A nova senha deve ter pelo menos 8 caracteres.',
            });
            return;
        }

        if (!hasNameChange && !hasPasswordChange) {
            setFeedback({
                type: 'error',
                message: 'Altere pelo menos o nome ou a senha antes de salvar.',
            });
            return;
        }

        try {
            setSavingProfile(true);

            await userApi.updateProfile({
                ...(hasNameChange ? { name: trimmedName } : {}),
                ...(hasPasswordChange ? { currentPassword, newPassword } : {}),
            });

            if (storedUser) {
                authUserStorage.set({
                    ...storedUser,
                    name: trimmedName,
                });
            }

            setCurrentPassword('');
            setNewPassword('');
            setFeedback({
                type: 'success',
                message: hasPasswordChange
                    ? 'Minha conta atualizada com sucesso. Nome e senha foram processados.'
                    : 'Nome atualizado com sucesso.',
            });
        } catch (err: any) {
            setFeedback({
                type: 'error',
                message: getApiErrorMessage(err, 'Não foi possível atualizar sua conta.'),
            });
        } finally {
            setSavingProfile(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-8 text-white">
                <div className="flex items-center gap-4">
                    <div className="rounded-2xl bg-white/10 p-3">
                        <User className="h-8 w-8" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Minha Conta</h1>
                        <p className="text-primary-100 mt-2">
                            Atualize seus dados de perfil e sua senha de acesso.
                        </p>
                    </div>
                </div>
            </div>

            {feedback && (
                <Alert
                    type={feedback.type}
                    title={feedback.type === 'success' ? 'Operação realizada' : 'Não foi possível concluir'}
                    message={feedback.message}
                />
            )}

            <section className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6 max-w-3xl">
                <div className="flex items-start gap-4">
                    <div className="rounded-2xl bg-primary-50 p-3">
                        <User className="h-6 w-6 text-primary-600" />
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold text-secondary-900">Dados da conta</h2>
                        <p className="mt-1 text-sm text-secondary-600">
                            Altere seu nome e, se quiser trocar a senha, informe a senha atual junto da nova senha.
                        </p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="mt-6 space-y-5">
                    <div>
                        <label htmlFor="profile-name" className="block text-sm font-medium text-secondary-700 mb-1">
                            Nome
                        </label>
                        <input
                            id="profile-name"
                            type="text"
                            className="input-field w-full"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={savingProfile}
                            placeholder="Digite seu nome"
                        />
                    </div>

                    <div className="rounded-2xl border border-secondary-200 bg-secondary-50 p-5 space-y-4">
                        <div className="flex items-center gap-3">
                            <KeyRound className="h-5 w-5 text-secondary-700" />
                            <div>
                                <div className="text-sm font-medium text-secondary-900">Alteração de senha</div>
                                <p className="text-sm text-secondary-600">
                                    Preencha os campos abaixo apenas se quiser trocar sua senha agora.
                                </p>
                            </div>
                        </div>

                        <div>
                            <label htmlFor="current-password" className="block text-sm font-medium text-secondary-700 mb-1">
                                Senha atual
                            </label>
                            <input
                                id="current-password"
                                type="password"
                                className="input-field w-full"
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                disabled={savingProfile}
                                placeholder="Digite sua senha atual"
                            />
                        </div>

                        <div>
                            <label htmlFor="new-password" className="block text-sm font-medium text-secondary-700 mb-1">
                                Nova senha
                            </label>
                            <input
                                id="new-password"
                                type="password"
                                className="input-field w-full"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={savingProfile}
                                placeholder="Digite a nova senha"
                                minLength={8}
                            />
                            <p className="mt-1 text-xs text-secondary-500">
                                Mínimo de 8 caracteres.
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button type="submit" className="btn-primary min-w-[190px] inline-flex items-center justify-center gap-2" disabled={savingProfile}>
                            <Save className="h-4 w-4" />
                            {savingProfile ? 'Salvando...' : 'Salvar alterações'}
                        </button>
                    </div>
                </form>
            </section>
        </div>
    );
};

export default Profile;
